import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { UtilsService } from './utils/utils.service';
import { BaseResponseModel } from '../models/BaseResponseModel';
import { MonthlyShiftData } from '../models/newModels/MonthlyShiftData';
import { catchError, map } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CallOutReasonModel } from '../models/newModels/CallOutReasonModel';
import { JavaPayload } from '../models/newModels/JavaPayload';
import { ShiftCancelData } from '../models/shift-management-models/cancel-shift-Data.model';
import {
  GetShiftsRequest,
  GetShiftsResponse,
  RemoveStaffReason
} from '../models/java-models';
import { JavaBaseResponse } from '../models/shift-management-models/java-base-response';
import { StartOfWeek } from '../models/shift-management-models/StartOfWeek';
import { ShiftHistoryResponse } from '../models/ShiftHistoryResponse';
import { BillingPayload } from '../models/newModels/BIllingPayload';
import { FloorInfo } from '../models/FloorInfo';
import { ShiftTime } from '../models/ShiftTime';
import { ClientShiftTime } from '../models/ClientShiftTime';
import { ShiftTimeRequest } from '../models/ShiftTimeRequest';
import { CreateShiftResponse } from '../models/CreateShiftResponse';
import { IntelyproShiftRequest } from '../models/IntelyproShiftRequest';

/**
 * ShiftManagementService manages shift operations (get,update,post) in database.
 */

const formUrlEncoded = {
  headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8')
};

@Injectable({
  providedIn: 'root'
})
export class ShiftManagementService {
  private _isystem = this._utilService.getISystemBaseUri();
  private _shiftJavaUri = this._utilService.getShiftJavaUri();
  private _icareUri = this._utilService.getICareUri();
  private _shiftManagementUri = this._utilService.getShiftManagementJavaUri();
  constructor(
    private _http: HttpClient,
    private _snackbar: MatSnackBar,
    private _utilService: UtilsService
  ) {}

  /**
   * Get all open shifts.
   * @param params : Shift's details
   */
  public getShifts(filters: GetShiftsRequest): Observable<GetShiftsResponse> {
    const arrayOfKeys: string[] = Object.keys(filters);
    let params: HttpParams = new HttpParams();
    arrayOfKeys.forEach((key: string) => {
      params = params.append(key, String(filters[key]));
    });
    return this._http.get<JavaPayload<GetShiftsResponse>>(`${this._shiftJavaUri}/shifts/`, { params }).pipe(
      map((resp: JavaPayload<GetShiftsResponse>) => resp.data),
      catchError(error => {
        this._snackbar.open('An error has occured. Please try again at a later time.', 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }

  /**
   * Get's callout reasons.
   */
  public getCalloutReasons(): Observable<CallOutReasonModel[]> {
    return this._http.get<JavaPayload<CallOutReasonModel[]>>(`${this._shiftJavaUri}/calloutReasons/`).pipe(
      map((response: JavaPayload<CallOutReasonModel[]>) => response.data),
      catchError(error => {
        this._snackbar.open('An error has occured. Please try again at a later time.', 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }

  /**
   * Get's cancel data for a shift.
   * @param reqdid : Shift's request ID
   * @param uid : User ID
   */
  public getShiftCancelData(reqdid: number, uid: number): Observable<ShiftCancelData> {
    const params = new FormData();
    params.append('reqdid', String(reqdid));
    params.append('uid', String(uid));
    return this._http.post<BaseResponseModel<ShiftCancelData>>(`${this._isystem}/pages/shiftCancelDet.php`, params).pipe(
      map((response: BaseResponseModel<ShiftCancelData>) => response.data),
      catchError(error => {
        this._snackbar.open('An error has occured. Please try again at a later time.', 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }

  /**
   * Removes a staff from a shift with reason.
   * @param staffID : Staff ID
   * @param shiftID : Shift ID
   * @param calloutReasonID : Reason ID
   */
  public removeStaffWithReason(staffID: number, shiftID: number, calloutReasonID: number, notes?: string, reqId?: number): Observable<RemoveStaffReason> {
    const body: Object = <Object>{
      'reqId': reqId,
      'staffId': staffID,
      'shiftId': shiftID,
      'reasonId': calloutReasonID,
      'Notes': notes
    };
    return this._http.post<JavaPayload<RemoveStaffReason>>(`${this._shiftJavaUri}/callout`, body).pipe(
      map((resp: JavaPayload<RemoveStaffReason>) => resp.data),
      catchError(error => {
        this._snackbar.open(error.error.message, 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }

  public getMonthlyShiftsInfo(startDate: string, endDate: string): Observable<BaseResponseModel<MonthlyShiftData[]>> {
    return this._http.get<BaseResponseModel<MonthlyShiftData[]>>(`${this._shiftJavaUri}/shift/range/start/${startDate}/end/${endDate}`);
  }

  getWeekStartAndDOW(): Observable<JavaBaseResponse<StartOfWeek>> {
    return this._http.get<JavaBaseResponse<StartOfWeek>>(`${this._shiftJavaUri}/settings/startweek/info`);
  }


  postShiftHistoryI(reqdid: number, type: string, uid: number): Observable<ShiftHistoryResponse[]> {
    const requestBody = new URLSearchParams();
    requestBody.set('reqdid', String(reqdid));
    requestBody.set('type', type);
    requestBody.set('uid', String(uid));
    return this._http.post<{ code: number, data: ShiftHistoryResponse[] }>(`${this._isystem}/manage/reports.php`,
      String(requestBody), formUrlEncoded)
      .pipe(
        map(resp => resp.data)
      );
  }

  postViewStatus(section: string, cid: number, reqdid: number, uid: number): Observable<any> {
    const requestBody = new URLSearchParams();
    requestBody.set('section', section);
    requestBody.set('cid', String(cid));
    requestBody.set('reqdid', String(reqdid));
    requestBody.set('uid', String(uid));
    return this._http.post<any[]>(`${this._isystem}/pages/partcare.php`, String(requestBody), formUrlEncoded);
  }

  updateRevisons(formData): Observable<BillingPayload> {
    const requestBody = new URLSearchParams();
    Object.keys(formData).forEach(key => {
      requestBody.set(key, formData[key]);
    })
    return this._http.post<BillingPayload>(`${this._isystem}/manage/billing.php`, String(requestBody), formUrlEncoded)
    .pipe(
      catchError(error => {
        this._snackbar.open(error?.message, 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }

  /**
   * Changes the floor for a shift request after it has been accepted
   * @param reqdid The request ID of the shift
   * @param floorId The new floor ID
   */
  public changeShiftFloor(reqdid: number, floorId: number): Observable<JavaPayload<string>> {
    const body = {
      Section: 'floor',
      reqdid,
      'floorid': floorId
    };
    return this._http.post<any>(`${this._isystem}/OPS/uprequest.php`, body).pipe(
      catchError(error => {
        this._snackbar.open(error?.message || 'An error has occurred while changing the shift location', 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }


  /**
   * Retrieves the client's active floors from icaredataservice. Floors deactivated on the
   * Hub are left out, so the caller only ever sees floors that can still take shifts.
   * @param clientId The client ID for which to fetch floors.
   * @returns An Observable emitting an array of FloorInfo objects.
   */
  public getFloors(clientId: number): Observable<FloorInfo[]> {
    return this._http.get<JavaBaseResponse<FloorInfo[]>>(`${this._icareUri}/client-floors/clientId/${clientId}/active`).pipe(
      map((resp: JavaBaseResponse<FloorInfo[]>) => resp.data || [])
    );
  }


  /**
   * Adds a floor through icaredataservice. The floor is created for the client on the
   * caller's token, and the remaining floor details are left empty the way the isystem
   * add did: this screen only collects a name.
   * @param floorName The name of the floor to add.
   * @returns An Observable emitting the icaredataservice response, whose data is the new floor id.
   */
  public addFloor(floorName: string): Observable<JavaBaseResponse<number>> {
    const floor: Partial<FloorInfo> = {
      floor: floorName,
      location: '',
      service: '',
      serviceNotes: '',
      beds: 0,
      bedNotes: '',
      active: true
    };
    return this._http.post<JavaBaseResponse<number>>(`${this._icareUri}/client-floors`, floor);
  }


  /**
   * Renames a floor through icaredataservice. The client the floor has to belong to is
   * taken from the caller's token, and a name the service rejects comes back as an HTTP
   * error rather than a 200 carrying a failure code.
   * @param floorId The floor ID to rename.
   * @param floorName The new floor name. Encoded because it travels as a path segment.
   * @returns An Observable emitting the icaredataservice response.
   */
  public renameFloor(floorId: number, floorName: string): Observable<JavaBaseResponse<void>> {
    return this._http.put<JavaBaseResponse<void>>(
      `${this._icareUri}/client-floors/floorId/${floorId}/floorName/${encodeURIComponent(floorName)}`,
      {}
    );
  }


  /**
   * Retrieves all available shift times for the client.
   * @returns An Observable emitting an array of ShiftTime objects.
   */
  public getShiftTimes(): Observable<ShiftTime[]> {
    return this._http.get<JavaBaseResponse<ShiftTime[]>>(`${this._icareUri}/shift-times/client`).pipe(
      map(resp => resp.data)
    );
  }


  /**
   * Retrieves the shift times shown on Settings > Shift Times: the client's enabled
   * shift times plus the default shift times, which stay listed while disabled so
   * they can be re-enabled.
   * @returns An Observable emitting an array of ClientShiftTime objects.
   */
  public getAvailableShiftTimes(): Observable<ClientShiftTime[]> {
    return this._http.get<JavaBaseResponse<ClientShiftTime[]>>(`${this._icareUri}/shift-times/client/available`).pipe(
      map(resp => resp.data)
    );
  }

  /**
   * Adds shift times for the current client via icaredataservice.
   * @param requests The shift times (start/end) to add.
   * @returns An Observable emitting the ids of the added shift times.
   */
  public addShiftTimesForClient(requests: ShiftTimeRequest[]): Observable<JavaBaseResponse<number[]>> {
    return this._http.post<JavaBaseResponse<number[]>>(`${this._icareUri}/shift-times/client`, requests);
  }


  /**
   * Removes a shift time from the current client via icaredataservice.
   * @param shiftTimeId The id of the shift time to remove.
   * @returns An Observable emitting the number of records removed.
   */
  public removeShiftTimeForClient(shiftTimeId: number): Observable<JavaBaseResponse<number>> {
    return this._http.delete<JavaBaseResponse<number>>(`${this._icareUri}/shift-times/client/shift-time/${shiftTimeId}`);
  }


  /**
   * Creates new Intelypro shifts using the provided shift requests.
   * @param requests An array of IntelyproShiftRequest objects containing shift details.
   * @returns An Observable emitting the JavaPayload containing the CreateShiftResponse.
   */
  public createIntelyproShifts(requests: IntelyproShiftRequest[]): Observable<JavaPayload<CreateShiftResponse>> {
    return this._http.post<JavaPayload<CreateShiftResponse>>(`${this._shiftManagementUri}/create-shift/client-platform`, requests).pipe(
      map((resp: JavaPayload<CreateShiftResponse>) => resp),
      catchError(error => {
        this._snackbar.open(error.error.message, 'X', {
          duration: 3000
        });
        return throwError(error);
      })
    );
  }
}
